import { CommodityGroup, OfferTemplate } from "../../types/shop";

export type TemplateRules = {
	slots: readonly GroupRule[];
	transitions?: ReadonlyMap<CommodityGroup, ReadonlySet<CommodityGroup>>;
	allowedStart?: ReadonlySet<CommodityGroup>;
};

const set = <T>(...v: T[]): ReadonlySet<T> => new Set<T>(v);

export const ITEM_COUNT_LIMITS: Record<
	CommodityGroup,
	{ min: number; max: number }
> = {
	[CommodityGroup.BUILDING]: { min: 1, max: 5 },
	[CommodityGroup.UPGRADE]: { min: 1, max: 2 },
	[CommodityGroup.RESOURCE]: { min: 1, max: 6 },
	[CommodityGroup.UTILITY]: { min: 1, max: 2 },
	[CommodityGroup.EVENT_RESOURCE]: { min: 1, max: 3 },
};

export enum RewardCompositionMode {
	SINGLE_GROUP = "single",
	MIXED_GROUPS = "mix",
}

export type GroupRule = {
	mode: RewardCompositionMode;
	allowed: ReadonlySet<CommodityGroup>;
	perGroupLimits?: Partial<
		Record<CommodityGroup, { min: number; max: number }>
	>;
	allowTextWhenSingle?: ReadonlySet<CommodityGroup>;
};

export const OFFER_TEMPLATE_RULES: Record<OfferTemplate, TemplateRules | null> =
	{
		[OfferTemplate.SLOTS_1]: {
			slots: [
				{
					mode: RewardCompositionMode.SINGLE_GROUP,
					allowed: set(
						CommodityGroup.RESOURCE,
						CommodityGroup.UTILITY,
					),
					perGroupLimits: {
						[CommodityGroup.RESOURCE]: { min: 1, max: 6 },
						[CommodityGroup.UTILITY]: { min: 1, max: 6 },
					},
				},
			],
		},

		[OfferTemplate.SLOTS_2]: {
			slots: [
				// TOP slot: mix groups with per-group limits
				{
					mode: RewardCompositionMode.SINGLE_GROUP,
					allowed: set(
						CommodityGroup.BUILDING,
						CommodityGroup.UPGRADE,
						CommodityGroup.RESOURCE,
						CommodityGroup.UTILITY,
					),
					perGroupLimits: {
						[CommodityGroup.BUILDING]: { min: 1, max: 5 },
						[CommodityGroup.UPGRADE]: { min: 1, max: 2 },
						[CommodityGroup.RESOURCE]: { min: 1, max: 6 },
						[CommodityGroup.UTILITY]: { min: 1, max: 2 },
					},
					// TODO delete all allowsTextWhenSingle
					allowTextWhenSingle: set(
						CommodityGroup.BUILDING,
						CommodityGroup.UPGRADE,
					),
				},
				// BOTTOM slot: single group only (one commodity group)
				{
					mode: RewardCompositionMode.SINGLE_GROUP,
					allowed: set(
						CommodityGroup.RESOURCE,
						CommodityGroup.UTILITY,
						CommodityGroup.EVENT_RESOURCE,
					),
					perGroupLimits: {
						[CommodityGroup.RESOURCE]: { min: 1, max: 6 },
						[CommodityGroup.UTILITY]: { min: 1, max: 2 },
						[CommodityGroup.EVENT_RESOURCE]: { min: 1, max: 3 },
					},
				},
			],
		},

		[OfferTemplate.SLOTS_3]: {
			slots: [
				{
					mode: RewardCompositionMode.SINGLE_GROUP,
					allowed: set(
						CommodityGroup.BUILDING,
						CommodityGroup.UPGRADE,
						CommodityGroup.RESOURCE,
						CommodityGroup.UTILITY,
					),
					perGroupLimits: {
						[CommodityGroup.BUILDING]: { min: 1, max: 5 },
						[CommodityGroup.UPGRADE]: { min: 1, max: 2 },
						[CommodityGroup.RESOURCE]: { min: 1, max: 6 },
						[CommodityGroup.UTILITY]: { min: 1, max: 2 },
					},
					allowTextWhenSingle: set(
						CommodityGroup.BUILDING,
						CommodityGroup.UPGRADE,
					),
				},
				{
					mode: RewardCompositionMode.SINGLE_GROUP,
					allowed: set(
						CommodityGroup.UPGRADE,
						CommodityGroup.RESOURCE,
						CommodityGroup.UTILITY,
					),
					perGroupLimits: {
						[CommodityGroup.UPGRADE]: { min: 1, max: 2 },
						[CommodityGroup.RESOURCE]: { min: 1, max: 6 },
						[CommodityGroup.UTILITY]: { min: 1, max: 2 },
					},
				},
				{
					mode: RewardCompositionMode.SINGLE_GROUP,
					allowed: set(
						CommodityGroup.RESOURCE,
						CommodityGroup.UTILITY,
						CommodityGroup.EVENT_RESOURCE,
					),
					perGroupLimits: {
						[CommodityGroup.RESOURCE]: { min: 1, max: 6 },
						[CommodityGroup.UTILITY]: { min: 1, max: 2 },
						[CommodityGroup.EVENT_RESOURCE]: { min: 1, max: 3 },
					},
				},
			],
			allowedStart: set(
				CommodityGroup.BUILDING,
				CommodityGroup.UPGRADE,
				CommodityGroup.RESOURCE,
			),
			transitions: new Map([
				[
					CommodityGroup.BUILDING,
					set(
						CommodityGroup.UPGRADE,
						CommodityGroup.RESOURCE,
						CommodityGroup.UTILITY,
					),
				],
				[
					CommodityGroup.UPGRADE,
					set(
						CommodityGroup.RESOURCE,
						CommodityGroup.UTILITY,
						CommodityGroup.EVENT_RESOURCE,
					),
				],
				[
					CommodityGroup.RESOURCE,
					set(CommodityGroup.UTILITY, CommodityGroup.EVENT_RESOURCE),
				],
				[
					CommodityGroup.UTILITY,
					set(CommodityGroup.RESOURCE, CommodityGroup.EVENT_RESOURCE),
				],
				[CommodityGroup.EVENT_RESOURCE, set()],
			]),
		},
		[OfferTemplate.SLOTS_4]: {
			slots: [
				{
					mode: RewardCompositionMode.SINGLE_GROUP,
					allowed: set(
						CommodityGroup.BUILDING,
						CommodityGroup.UPGRADE,
					),
					perGroupLimits: {
						[CommodityGroup.BUILDING]: { min: 1, max: 5 },
						[CommodityGroup.UPGRADE]: { min: 1, max: 2 },
					},
					allowTextWhenSingle: set(
						CommodityGroup.BUILDING,
						CommodityGroup.UPGRADE,
					),
				},
				{
					mode: RewardCompositionMode.SINGLE_GROUP,
					allowed: set(
						CommodityGroup.UPGRADE,
						CommodityGroup.RESOURCE,
					),
					perGroupLimits: {
						[CommodityGroup.UPGRADE]: { min: 1, max: 2 },
						[CommodityGroup.RESOURCE]: { min: 1, max: 6 },
					},
				},
				{
					mode: RewardCompositionMode.SINGLE_GROUP,
					allowed: set(
						CommodityGroup.RESOURCE,
						CommodityGroup.UTILITY,
					),
					perGroupLimits: {
						[CommodityGroup.RESOURCE]: { min: 1, max: 6 },
						[CommodityGroup.UTILITY]: { min: 1, max: 2 },
					},
				},
				{
					mode: RewardCompositionMode.SINGLE_GROUP,
					allowed: set(
						CommodityGroup.UTILITY,
						CommodityGroup.EVENT_RESOURCE,
					),
					perGroupLimits: {
						[CommodityGroup.UTILITY]: { min: 1, max: 2 },
						[CommodityGroup.EVENT_RESOURCE]: { min: 1, max: 3 },
					},
				},
			],
			allowedStart: set(CommodityGroup.BUILDING, CommodityGroup.UPGRADE),
			transitions: new Map([
				[CommodityGroup.BUILDING, set(CommodityGroup.UPGRADE)],
				[
					CommodityGroup.UPGRADE,
					set(CommodityGroup.RESOURCE, CommodityGroup.UTILITY),
				],
				[
					CommodityGroup.RESOURCE,
					set(CommodityGroup.UTILITY, CommodityGroup.EVENT_RESOURCE),
				],
				[CommodityGroup.UTILITY, set(CommodityGroup.EVENT_RESOURCE)],
				[CommodityGroup.EVENT_RESOURCE, set()],
			]),
		},
		[OfferTemplate.PIGGY_BANK]: {
			// INFO: Single slot only; represents the actual reward claimed after purchase
			slots: [
				{
					mode: RewardCompositionMode.SINGLE_GROUP,
					allowed: set(
						CommodityGroup.RESOURCE,
						CommodityGroup.UTILITY,
						CommodityGroup.EVENT_RESOURCE,
					),
					perGroupLimits: {
						[CommodityGroup.RESOURCE]: { min: 1, max: 1 },
						[CommodityGroup.UTILITY]: { min: 1, max: 1 },
						[CommodityGroup.EVENT_RESOURCE]: { min: 1, max: 1 },
					},
				},
			],
		},
		// TODO DS: ??
		[OfferTemplate.BUNDLE]: {
			slots: [],
		},
		[OfferTemplate.EVENT_PASS]: null,
		[OfferTemplate.PROVISION]: null,
	};
